import { CallIntelTypes } from '@frontend/api-call-intel';
import { theme } from '@frontend/theme';
import { ComputeRelativeChangeOptions } from '../types';

export const callIntelKpiUtils = {
  toPercent: (part = 0, whole = 0): number => (whole > 0 ? (part / whole) * 100 : 0),
  computeRelativeChange: ({
    currentBaseCount,
    currentPercent,
    previousBaseCount,
    previousPercent,
    options,
  }: {
    currentBaseCount?: number;
    currentPercent?: number;
    previousBaseCount?: number;
    previousPercent?: number;
    options?: ComputeRelativeChangeOptions;
  }): { change: CallIntelTypes.ChangeType; percentageChange?: number } => {
    const { epsilon = 0, precisionStrategy = callIntelKpiUtils.formatPercentWithPrecision } = options ?? {};

    // No analysis possible if base counts are 0/undefined
    if (!currentBaseCount || !previousBaseCount) return { change: 'noData' };

    if (previousPercent == null || Number.isNaN(previousPercent)) return { change: 'noData' };
    if (currentPercent == null || Number.isNaN(currentPercent)) return { change: 'noData' };

    const relativeChange = currentPercent - previousPercent;
    const absChange = Math.abs(relativeChange);

    if (absChange <= epsilon) {
      return { change: 'unchanged', percentageChange: 0 };
    }

    const formattedChange = precisionStrategy(absChange);

    return {
      change: relativeChange > 0 ? 'increased' : 'decreased',
      percentageChange: formattedChange,
    };
  },
  formatValue: (value: number | null, format: CallIntelTypes.KpiItem['format']) => {
    if (value === null || Number.isNaN(value)) return '—';
    if (typeof format === 'function') return format(value);
    if (format === 'percent') return `${value}%`;
    return Intl.NumberFormat('en-US').format(value);
  },
  formatPercentWithPrecision: (value: number): number => {
    if (value <= 0) return 0;

    // For positive values < 1%, always show at least 0.1%
    if (value < 1) {
      const rounded = Number(value.toFixed(1));
      return rounded === 0 ? 0.1 : rounded;
    }

    return Math.round(value);
  },
  getTrendColor: (change: CallIntelTypes.ChangeType, polarity: CallIntelTypes.Polarity) => {
    if (polarity === 'neutral' || change === 'unchanged') return theme.colors.neutral50;
    if (change === 'increased') return polarity === 'upGood' ? theme.colors.success50 : theme.colors.critical50;
    if (change === 'decreased') return polarity === 'downGood' ? theme.colors.success50 : theme.colors.critical50;
    return theme.colors.neutral60; // 'noData'
  },
};
